import { Alert, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import ExportModalDataWrapper from 'src/app/components/data-wrappers/tickets/ExportModalDataWrapper';
import AddContractFeature from 'src/app/components/features/purchase/AddContractFeature';
import TicketLinkImportManagementFeature from 'src/app/components/features/ticket-link-imports/TicketLinkImportManagementFeature';
import TicketsTableFeature from 'src/app/components/features/tickets/TicketsTableFeature';
import TicketsUrlService from 'src/app/components/features/tickets/TicketsUrlService';
import { FETCH_EVENT_TICKETS_QUERY_KEY } from 'src/app/hooks/tickets/useFetchEventTickets';
import UserPermissions from 'src/app/utilities/helpers/userPermissions';
import { useSpacingStyles } from 'src/shared/styles/spacingStyles';
import Button from 'src/view/components/button/Button';
import { ETBContainer } from 'src/view/components/container/container';
import Header from 'src/view/components/header/Header';
import Modal from 'src/view/components/modal/Modal';

interface TicketsOverviewProps {
    eventName: string;
}

type ActiveModal = 'export' | 'fileImport' | 'addContracts' | null;

const TicketsOverview = ({ eventName }: TicketsOverviewProps) => {
    const spacingClasses = useSpacingStyles();
    const queryClient = useQueryClient();
    const { eventId } = useParams<{ eventId: string }>();
    const [ticketsInvalidatedAt, setTicketsInvalidatedAt] = useState<number | undefined>();
    const [activeModal, setActiveModal] = useState<ActiveModal>(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleTicketUpdate = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: [FETCH_EVENT_TICKETS_QUERY_KEY],
                exact: false,
            }),
        [queryClient]
    );

    const onContractCreated = useCallback(() => {
        enqueueSnackbar('Contract tickets added', { variant: 'success' });
        setActiveModal(null);
        setTicketsInvalidatedAt(Date.now());
        handleTicketUpdate();
    }, [enqueueSnackbar, handleTicketUpdate]);

    if (!eventId) {
        return <Alert severity="error">No eventId was found</Alert>;
    }

    return (
        <>
            <Header>
                <Grid item>
                    <h1>{eventName}</h1>
                </Grid>
                <Grid item>
                    <Button
                        onClick={() => setActiveModal('fileImport')}
                        className={spacingClasses.spacingRight}
                    >
                        File Imports
                    </Button>
                    <Button
                        onClick={() => setActiveModal('export')}
                        variant="text"
                        white
                        className={spacingClasses.spacingRight}
                    >
                        Exports
                    </Button>
                    <Button
                        onClick={() => setActiveModal('addContracts')}
                        className={spacingClasses.spacingRight}
                    >
                        Add contract ticket(s)
                    </Button>
                    <Button
                        href={TicketsUrlService.batchUploadTickets(eventId)}
                        className={spacingClasses.spacingRight}
                    >
                        Batch upload tickets
                    </Button>
                </Grid>
            </Header>
            <ETBContainer>
                <TicketsTableFeature
                    eventId={eventId}
                    invalidatedAt={ticketsInvalidatedAt}
                    enableDeleteTickets={UserPermissions.canDeleteTicket()}
                    enableBatchEditTickets
                    onSuccess={handleTicketUpdate}
                />

                <Modal
                    open={activeModal === 'export'}
                    title="Export Tickets"
                    onClose={() => setActiveModal(null)}
                >
                    <div>
                        Export tickets by clicking the download button
                        <ExportModalDataWrapper eventId={eventId} />
                    </div>
                </Modal>

                <Modal
                    open={activeModal === 'fileImport'}
                    title="Import ticket links"
                    width="x-large"
                    onClose={() => {
                        setActiveModal(null);
                        handleTicketUpdate();
                    }}
                >
                    <TicketLinkImportManagementFeature eventId={eventId} />
                </Modal>

                <Modal
                    title="Add contract tickets"
                    open={activeModal === 'addContracts'}
                    width="large"
                    onClose={() => {
                        setActiveModal(null);
                    }}
                >
                    <AddContractFeature eventId={eventId} onCreated={onContractCreated} />
                </Modal>
            </ETBContainer>
        </>
    );
};

export default TicketsOverview;
